import { useMutationState } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { orderEditsKey, useOrderEditor, type OrderEdit, type OrderRef } from './useRunningOrder.ts'

export type SaveState = 'idle' | 'saving' | 'saved' | 'failed'

/** How long "Saved" stays after a change lands, in ms. */
const SAVED_FOR = 2000

/**
 * Whether the plan's last change saved: one answer for every panel on the
 * page, because each panel's editor files its changes under the plan
 * (`servicesKeys.planEdits`). `saving` while one is on its way; `saved` for a
 * moment after; `failed` with the error until it is retried (or another
 * change saves). A failed change is still in the field it was typed in, and
 * `retry` sends exactly it again.
 *
 * `only` narrows it to some kinds of change (a panel that edits items reads
 * just `updateItem`); removals are left out by default, because they report
 * through their own Undo notice.
 */
export function usePlanSaveState(planId: string, only?: readonly OrderEdit[]) {
  return useOrderSaveState({ kind: 'plan', id: planId }, only)
}

/** The same, for a plan's or a template's running order (`OrderRef`). */
export function useOrderSaveState(ref: OrderRef, only?: readonly OrderEdit[]) {
  const editor = useOrderEditor(ref)
  const edits = useMutationState({
    filters: { mutationKey: orderEditsKey(ref) },
    select: (mutation) => ({
      edit: mutation.options.mutationKey?.[3] as OrderEdit | undefined,
      status: mutation.state.status,
      error: mutation.state.error,
      variables: mutation.state.variables,
      at: mutation.state.submittedAt,
    }),
  })
  const kinds =
    only ??
    ([
      'updateItem',
      'updatePlan',
      'updateTemplate',
      'addItem',
      'reorder',
      'goLive',
      'setNeed',
    ] as const)
  const latest = edits
    .filter((e) => e.edit !== undefined && (kinds as readonly string[]).includes(e.edit))
    .reduce<(typeof edits)[number] | null>(
      (newest, e) => (newest === null || e.at >= newest.at ? e : newest),
      null,
    )

  // "Saved" shows for a moment, then the line goes quiet.
  const [now, setNow] = useState(() => Date.now())
  const savedAt = latest?.status === 'success' ? latest.at : null
  useEffect(() => {
    if (savedAt === null) return
    setNow(Date.now())
    const timer = setTimeout(() => setNow(Date.now()), SAVED_FOR + 50)
    return () => clearTimeout(timer)
  }, [savedAt])

  const state: SaveState =
    latest === null
      ? 'idle'
      : latest.status === 'pending'
        ? 'saving'
        : latest.status === 'error'
          ? 'failed'
          : latest.status === 'success' && now - latest.at < SAVED_FOR
            ? 'saved'
            : 'idle'

  const retry = () => {
    if (!latest?.edit || latest.status !== 'error') return
    // Each edit takes its own variables; these are the failed edit's own.
    const mutation = editor.mutations[latest.edit] as
      { mutate: (variables: unknown) => void } | undefined
    mutation?.mutate(latest.variables)
  }

  return {
    state,
    error: latest?.status === 'error' ? latest.error : null,
    /** Which kind of change failed, for words: "the length", "the order". */
    failedEdit: latest?.status === 'error' ? (latest.edit ?? null) : null,
    retry,
  }
}
